import {
  ProductFilter,
  ProductFilterSortOrderEnum,
  ProductFilterSeasonsEnum,
  SizeSizeTypeEnum,
  type Size,
} from '@trwl/backend.api';
import { deserializeFilterFromQuery } from '@trwl/store/filter/deserializer';
import { serializeFilterToQuery } from '@trwl/store/filter/serializer';
import { createEvent, createStore, merge } from 'effector';
import { persist } from 'effector-storage/query';
import isEqual from 'lodash-es/isEqual';

export const FIT_IDS: { ids: number[]; name: string }[] = [
  {
    ids: [1, 2],
    name: 'Мужской',
  },
  {
    ids: [1, 3],
    name: 'Женский',
  },
  {
    ids: [4, 5, 6, 7],
    name: 'Дети',
  },
];

export const SEASONS: { value: ProductFilterSeasonsEnum; name: string }[] = [
  {
    value: ProductFilterSeasonsEnum.Winter,
    name: 'Зима',
  },
  {
    value: ProductFilterSeasonsEnum.Spring,
    name: 'Весна',
  },
  {
    value: ProductFilterSeasonsEnum.Summer,
    name: 'Лето',
  },
  {
    value: ProductFilterSeasonsEnum.Autumn,
    name: 'Осень',
  },
];

export const SIZES: { type: SizeSizeTypeEnum; values: string[] }[] = [
  {
    type: SizeSizeTypeEnum.Eu,
    values: [
      '34',
      '34.5',
      '35',
      '35.5',
      '36',
      '36.5',
      '37',
      '37.5',
      '38',
      '38.5',
      '39',
      '39.5',
      '40',
      '40.5',
      '41',
      '41.5',
      '42',
      '42.5',
      '43',
      '43.5',
      '44',
      '44.5',
      '45',
      '45.5',
      '46',
      '46.5',
      '47',
      '47.5',
      '48',
      '48.5',
      '49',
      '49.5',
      '50',
      '50.5',
      '51',
      '51.5',
      '52',
      '52.5',
      '53',
      '53.5',
      '54',
      '54.5',
      '55',
      '55.5',
    ],
  },
  {
    type: SizeSizeTypeEnum.Fr,
    values: [
      '34',
      '34 ⅔',
      '35 ⅓',
      '36',
      '36 ⅔',
      '37 ⅓',
      '38',
      '38 ⅔',
      '39 ⅓',
      '40',
      '40 ⅔',
      '41 ⅓',
      '42',
      '42 ⅔',
      '43 ⅓',
      '44',
      '44 ⅔',
      '45 ⅓',
      '46',
      '46 ⅔',
      '47 ⅓',
      '48',
      '48 ⅔',
      '49 ⅓',
      '50',
      '50 ⅔',
      '51 ⅓',
      '52 ⅔',
      '53 ⅓',
      '54 ⅔',
      '55 ⅔',
      '56 ⅓',
    ],
  },
  {
    type: SizeSizeTypeEnum.Us,
    values: [
      '3',
      '3.5',
      '4',
      '4.5',
      '5',
      '5.5',
      '6',
      '6.5',
      '7',
      '7.5',
      '8',
      '8.5',
      '9',
      '9.5',
      '10',
      '10.5',
      '11',
      '11.5',
      '12',
      '12.5',
      '13',
      '13.5',
      '14',
      '14.5',
      '15',
      '16',
      '17',
      '18',
    ],
  },
  {
    type: SizeSizeTypeEnum.Uk,
    values: [
      '2.5',
      '3',
      '3.5',
      '4',
      '4.5',
      '5',
      '5.5',
      '6',
      '6.5',
      '7',
      '7.5',
      '8',
      '8.5',
      '9',
      '9.5',
      '10',
      '10.5',
      '11',
      '11.5',
      '12',
      '12.5',
      '13',
      '13.5',
      '14',
      '14.5',
      '15',
      '16',
      '17',
    ],
  },
];

export const DEFAULT_FILTER: Omit<ProductFilter, 'page'> = {
  size: 40,
  categoryIds: [],
  brandIds: [],
  sizeIds: [],
  seriesIds: [],
  fitIds: [],
  seasons: [],
  stock: true,
  sizes: [],
};
export const $filter = createStore<Omit<ProductFilter, 'page'>>(DEFAULT_FILTER);
const clearFilter = createEvent();
const setBrandIds = createEvent<number[]>();
const addBrandId = createEvent<number>();
const removeBrandId = createEvent<number>();
const setSeriesIds = createEvent<number[]>();
const addSeriesId = createEvent<number>();
const removeSeriesId = createEvent<number>();
const setFitIds = createEvent<number[]>();
const addFitId = createEvent<number>();
const removeFitId = createEvent<number>();
const addSeason = createEvent<ProductFilterSeasonsEnum>();
const removeSeason = createEvent<ProductFilterSeasonsEnum>();
const setSize = createEvent<{
  sizeType: SizeSizeTypeEnum;
  sizeValue: string;
}>();
const addSize = createEvent<Size>();
const removeSize = createEvent<Size>();
const setCategoryIds = createEvent<number[]>();
const addCategoryId = createEvent<number>();
const removeCategoryId = createEvent<number>();
const setStock = createEvent<boolean | undefined>();
const setMinPrice = createEvent<number>();
const setMaxPrice = createEvent<number>();
const setFilter = createEvent<ProductFilter>();
const setPartialFilter = createEvent<Partial<ProductFilter>>();
const setKeyword = createEvent<string | undefined>();
const setSort = createEvent<{
  order: ProductFilterSortOrderEnum | undefined;
  field: string;
}>();
const queryPicked = createEvent<{ queryString?: string; force?: boolean }>();

export const FILTERS = {
  clearFilter,
  setBrandIds,
  addBrandId,
  removeBrandId,
  setSeriesIds,
  addSeriesId,
  removeSeriesId,
  setFitIds,
  addFitId,
  removeFitId,
  addSeason,
  removeSeason,
  setCategoryIds,
  addCategoryId,
  removeCategoryId,
  setStock,
  setMinPrice,
  setMaxPrice,
  setSize,
  addSize,
  removeSize,
  setFilter,
  setPartialFilter,
  setKeyword,
  setSort,
  queryPicked,
};

$filter
  .on(setKeyword, (state, keyword) => ({ ...state, keyword }))
  .on(setBrandIds, (state, brandIds) => ({ ...state, brandIds }))
  .on(addBrandId, (state, brandId) => ({
    ...state,
    brandIds: state.brandIds ? [...state.brandIds, brandId] : [brandId],
  }))
  .on(removeBrandId, (state, brandId) => ({
    ...state,
    brandIds: state.brandIds
      ? state.brandIds.filter((id) => id !== brandId)
      : [],
  }))
  .on(setSeriesIds, (state, seriesIds) => ({ ...state, seriesIds }))
  .on(addSeriesId, (state, seriesId) => ({
    ...state,
    seriesIds: state.seriesIds ? [...state.seriesIds, seriesId] : [seriesId],
  }))
  .on(removeSeriesId, (state, seriesId) => ({
    ...state,
    seriesIds: state.seriesIds
      ? state.seriesIds.filter((id) => id !== seriesId)
      : [],
  }))
  .on(setFitIds, (state, fitIds) => ({ ...state, fitIds }))
  .on(addFitId, (state, fitId) => ({
    ...state,
    fitIds: state.fitIds ? [...state.fitIds, fitId] : [fitId],
  }))
  .on(removeFitId, (state, fitId) => ({
    ...state,
    fitIds: state.fitIds ? state.fitIds.filter((id) => id !== fitId) : [],
  }))
  .on(addSeason, (state, season) => ({
    ...state,
    seasons: state.seasons ? [...state.seasons, season] : [season],
  }))
  .on(removeSeason, (state, seasons) => ({
    ...state,
    seasons: state.seasons
      ? state.seasons.filter((value) => value !== seasons)
      : [],
  }))
  .on(setCategoryIds, (state, categoryIds) => ({ ...state, categoryIds }))
  .on(addCategoryId, (state, categoryId) => ({
    ...state,
    categoryIds: state.categoryIds
      ? [...state.categoryIds, categoryId]
      : [categoryId],
  }))
  .on(removeCategoryId, (state, categoryId) => ({
    ...state,
    categoryIds: state.categoryIds
      ? state.categoryIds.filter((id) => id !== categoryId)
      : [],
  }))
  .on(setStock, (state, stock) => ({ ...state, stock }))
  .on(setMinPrice, (state, minPrice) => ({ ...state, minPrice }))
  .on(setMaxPrice, (state, maxPrice) => ({ ...state, maxPrice }))
  .on(removeSize, (state, value) => ({
    ...state,
    sizes: state.sizes
      ? state.sizes.filter(
          (s) =>
            !(s.sizeType === value.sizeType && s.sizeValue === value.sizeValue),
        )
      : [],
  }))
  .on(addSize, (state, value) => ({
    ...state,
    sizes: state.sizes
      ? [...state.sizes.filter((s) => s.sizeType === value.sizeType), value]
      : [value],
  }))
  .on(setFilter, (_, filter) => filter)
  .on(setPartialFilter, (oldFilter, filter) => {
    const nextFilter = { ...oldFilter, ...filter };
    if (isEqual(oldFilter, nextFilter)) {
      return oldFilter;
    }
    return nextFilter;
  })
  .on(setSort, (state, sort) => {
    if (sort.order === undefined)
      return { ...state, sortOrder: undefined, sortField: undefined };
    return { ...state, sortOrder: sort.order, sortField: sort.field };
  })
  .on(clearFilter, () => {
    return DEFAULT_FILTER;
  });

const pickChanges = queryPicked.filter({
  fn: ({ queryString, force }) => {
    if (force) return true;
    if (!queryString) return false;

    const oldFilter = deserializeFilterFromQuery(
      serializeFilterToQuery($filter.getState()),
    );
    const newFilter = deserializeFilterFromQuery(queryString);
    return !isEqual(oldFilter, newFilter);
  },
});

export const filterUpdated = merge([
  clearFilter,
  setFilter,
  setPartialFilter,
  setKeyword,
  setBrandIds,
  addBrandId,
  removeBrandId,
  setSeriesIds,
  addSeriesId,
  removeSeriesId,
  setFitIds,
  addFitId,
  removeFitId,
  addSeason,
  removeSeason,
  setCategoryIds,
  addCategoryId,
  removeCategoryId,
  setStock,
  setMinPrice,
  setMaxPrice,
  setSize,
  addSize,
  removeSize,
  setSort,
  pickChanges,
]);

persist({
  store: $filter,
  serialize: serializeFilterToQuery,
  deserialize: deserializeFilterFromQuery,
  timeout: 300,
  key: 'filter',
  pickup: queryPicked,
});
